import {
  FilterOperator,
  FilterType,
  serializeFilters,
  type IActiveFilter,
} from '@moko/ui';

/** Max number of older versions to show in the history list. */
export const HISTORY_MAX = 10;

/**
 * History list rule (per PO): show up to HISTORY_MAX versions older than the
 * latest, in descending order. Returns empty for version <= 1.
 */
export const buildHistoryVersions = (latestVersion: number): number[] => {
  if (!Number.isFinite(latestVersion) || latestVersion <= 1) return [];
  const oldest = Math.max(1, latestVersion - HISTORY_MAX);
  const list: number[] = [];
  for (let v = latestVersion - 1; v >= oldest; v--) list.push(v);
  return list;
};

/**
 * Build the `filters=` query value for the metadata listing endpoint, scoped
 * to a single (entity, name) pair. Reuses the shared filter serializer so the
 * payload shape stays in sync with the listing page.
 */
export const buildEntityNameFilters = (
  entity: string,
  name: string
): string => {
  const filters: IActiveFilter[] = [
    {
      column: { key: 'entity', label: 'entity', type: FilterType.String },
      operator: FilterOperator.Eq,
      value: entity,
    },
    {
      column: { key: 'name', label: 'name', type: FilterType.String },
      operator: FilterOperator.Eq,
      value: name,
    },
  ];
  return serializeFilters(filters);
};

/**
 * Parse a positive integer version from a query-string value. Returns null for
 * missing, non-numeric, or non-positive values so callers can treat "viewing
 * latest" as the canonical default.
 */
export const parseVersionParam = (raw: string | null): number | null => {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
};
